import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Runtime contract for the "AH Prijsfavorieten" campaign dashboard payload.
 *
 * Transcribed from the design handoff (AH Prijsfavorieten Dashboard.dc.html):
 * a 21-day AH campaign of THREE weekly game rotations, with four sections —
 * Overzicht, Engagement,
 * Gameplay, Gebruikersgedrag. Separate report (and separate payload shape) from
 * the frozen AH campaign dashboard in features/ah.
 *
 * THIS SCHEMA IS THE STEP-2 CONTRACT: the pipeline must emit exactly this
 * shape. Notes for whoever implements it:
 *   - `days` are ISO dates; `series.*` arrays align index-for-index with it.
 *   - Values the backend can't express numerically are PRE-FORMATTED strings
 *     (timeInCampaign "152.320u", avgTimePerUser "17m 4s") — same convention
 *     the AH report already uses.
 *   - Counts stay numeric so the UI formats them nl-NL; percentages are
 *     numbers 0–100.
 *   - a weekly-table value is null for a week that hasn't run yet (renders "—").
 */

const campaignSchema = z.object({
  name: z.string(),
  /** Display strings, already localised by the pipeline (e.g. "24 aug 2026"). */
  start: z.string(),
  end: z.string(),
  /** Free-form status shown in the hero ("In voortgang"). */
  status: z.string(),
  currentDay: z.number(),
  totalDays: z.number(),
  /** Active campaign WEEK (1-based) — the hero reads "· WK 2". */
  currentWeek: z.number(),
  /** Hero tempo line, pre-formatted ("~200.137 games / dag"). */
  pace: z.string(),
  /** Hero "Weken" line ("3 × 1 week"). */
  weeksLabel: z.string(),
  /** Period the weeks belong to, shown on the strip ("Periode 1"). */
  periodLabel: z.string(),
  /** Calendar weeks covered, shown on the strip ("CW 35 / 36 / 37"). */
  calendarWeeks: z.string(),
  /** True while the campaign is running — drives the LIVE pill. */
  live: z.boolean().optional(),
});

// One campaign week on the strip: "wk 1" + "24 – 30 aug".
const weekSchema = z.object({
  label: z.string(),
  dates: z.string(),
  /** Not started yet → rendered muted with a "· gepland" suffix. */
  planned: z.boolean().optional(),
});

/*
 * "Overzicht per week" — the same five headline metrics, per campaign week.
 * Values are numbers (UI formats nl-NL), PRE-FORMATTED strings for the time row
 * ("84.905u"), or null for a week that hasn't run yet (rendered "—"). Same
 * mixed-value convention the AH report's per-venue `details` map uses.
 */
const weeklyRowSchema = z.object({
  label: z.string(),
  values: z.array(z.union([z.number(), z.string(), z.null()])),
});

const totalsSchema = z.object({
  uniqueVisitors: z.number(),
  avgVisitorsPerDay: z.number(),
  uniquePlayers: z.number(),
  /** TOTAAL AANTAL BEZOEKEN — sessions of unique visitors. */
  visits: z.number(),
  /** Pre-formatted total time in app ("152.320u"). */
  timeInCampaign: z.string(),
  /** Pre-formatted mean per user ("17m 4s"). */
  avgTimePerUser: z.string(),
  /** Pre-formatted mean session duration ("7m 17s"). */
  avgSessionDuration: z.string(),
  gamesPlayed: z.number(),
  avgGamesPerPlayer: z.number(),
});

/** A labelled share of players (VERDELING SPELERS / HERHAALGEDRAG rows). */
const shareRowSchema = z.object({ label: z.string(), pct: z.number() });

const gameplaySchema = z.object({
  /** How many games players play — 1 / 2 / 3 / 4 / 5+ games. */
  distribution: z.array(shareRowSchema),
  /** Repeat behaviour across campaign weeks. */
  repeat: z.object({
    /** Headline: share that played in every week of the campaign. */
    allWeeksPct: z.number(),
    rows: z.array(shareRowSchema),
  }),
});

const behaviourSchema = z.object({
  /** Informative pages opened, all pages combined. */
  infoPagesTotal: z.number(),
  topPages: z.array(z.object({ name: z.string(), value: z.number() })),
});

/*
 * One campaign PART (Deel 1 or Deel 2): 21 days, three weekly rotations. The
 * payload's top level is the part that is running; see `previous` below.
 */
export const prijsfavorietenPartSchema = z.object({
  campaign: campaignSchema,
  weeks: z.array(weekSchema),
  /** Per-week breakdown table (Overzicht per week). */
  weekly: z.object({ label: z.string(), columns: z.array(z.string()), rows: z.array(weeklyRowSchema) }),
  /** ISO dates, one per campaign day covered by the series. */
  days: z.array(z.string()),
  series: z.object({
    games: z.array(z.number()),
    visitors: z.array(z.number()),
  }),
  totals: totalsSchema,
  gameplay: gameplaySchema,
  behaviour: behaviourSchema,
});

export const prijsfavorietenPayloadSchema = prijsfavorietenPartSchema.extend({
  /*
   * The finished previous part — Deel 1 — in the same shape, for the Deel 1 vs
   * Deel 2 comparison. Its source data was deleted after it ended, so the
   * pipeline embeds a frozen copy (client-portal: pipelines/ah-prijsfavorieten/
   * snapshots/deel1.json). Absent on a payload from before Deel 2 existed; the
   * dashboard then renders the single-part layout.
   */
  previous: prijsfavorietenPartSchema.optional(),
});

export const prijsfavorietenEnvelopeSchema = z.object({
  data: prijsfavorietenPayloadSchema,
  meta: z.object({ last_refreshed_at: z.string().nullable() }).partial().optional(),
});

export type PrijsfavorietenPayload = z.infer<typeof prijsfavorietenPayloadSchema>;
export type PrijsfavorietenPart = z.infer<typeof prijsfavorietenPartSchema>;
export type PrijsfavorietenEnvelope = z.infer<typeof prijsfavorietenEnvelopeSchema>;
export type PrijsfavorietenWeek = z.infer<typeof weekSchema>;
export type WeeklyRow = z.infer<typeof weeklyRowSchema>;
export type ShareRow = z.infer<typeof shareRowSchema>;

/** Same generic report endpoint every dashboard uses: GET <base>/reports/<id>. */
export async function getPrijsfavorietenReport(reportId: string, baseUrl: string): Promise<PrijsfavorietenEnvelope> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}`).json();
  return prijsfavorietenEnvelopeSchema.parse(raw);
}
